
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Share2, Heart, Store } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import PointsTab from '@/components/profile/PointsTab';
import RecordingsTab from '@/components/profile/RecordingsTab';
import SharesTab from '@/components/profile/SharesTab';
import EventsTab from '@/components/profile/EventsTab';
import MerchantProfileTab from '@/components/profile/MerchantProfileTab';
import { Share } from '@/components/profile/SharesTab';
import { getSocialShares } from '@/services/databaseService';
import { Event } from '@/components/profile/EventsTab';
import { getMerchantById } from '@/services/merchantService';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [shares, setShares] = useState<Share[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isMerchant, setIsMerchant] = useState<boolean>(false);
  const [merchantInfo, setMerchantInfo] = useState<any>(null);
  
  // Get the active tab from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const defaultTab = queryParams.get('tab') || 'recordings';

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userFromLocal = localStorage.getItem('user');

        if (session?.user) {
          setUser(session.user);
          setUserId(session.user.id);
          fetchProfile(session.user.id);
          fetchShares(session.user.id);
          fetchEvents(session.user.id);
        } else if (userFromLocal) {
          const parsedUser = JSON.parse(userFromLocal);
          setUser(parsedUser);
          setUserId(parsedUser.id || parsedUser.sub);
          fetchProfile(parsedUser.id || parsedUser.sub);
          fetchShares(parsedUser.id || parsedUser.sub);
          fetchEvents(parsedUser.id || parsedUser.sub);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error retrieving user:', error);
      } finally {
        setLoading(false);
      }
    };

    getUser();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          setUserId(session.user.id);
          localStorage.setItem('user', JSON.stringify(session.user));
          fetchProfile(session.user.id);
          fetchShares(session.user.id);
          fetchEvents(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setUserId(null);
          localStorage.removeItem('user');
          setProfile(null);
          navigate('/login');
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profile')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
      } else {
        setProfile(data);
        setTotalPoints(data?.total_points || 0);
        
        // Check if user is a merchant
        if (data?.is_merchant) {
          setIsMerchant(true);
          fetchMerchantInfo(userId);
        }
        
        // Set up realtime subscription to listen for profile updates
        const channel = supabase
          .channel('profile-realtime-updates')
          .on('postgres_changes', 
            { 
              event: 'UPDATE', 
              schema: 'public', 
              table: 'profile',
              filter: `user_id=eq.${userId}`
            }, 
            (payload) => {
              if (payload.new) {
                setProfile(payload.new);
                if (typeof payload.new.total_points === 'number') {
                  setTotalPoints(payload.new.total_points);
                }
                if (payload.new.is_merchant !== undefined) {
                  setIsMerchant(payload.new.is_merchant);
                  if (payload.new.is_merchant && !merchantInfo) {
                    fetchMerchantInfo(userId);
                  }
                }
              }
            }
          )
          .subscribe();
          
        return () => {
          supabase.removeChannel(channel);
        };
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const fetchMerchantInfo = async (userId: string) => {
    try {
      // Try to find merchant information linked to this user
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .eq('merchant_id', userId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          console.log('No merchant profile found for user');
        } else {
          console.error('Error fetching merchant info:', error);
        }
        return;
      }
      
      if (data) {
        setMerchantInfo(data);
      }
    } catch (error) {
      console.error('Error fetching merchant info:', error);
    }
  };

  const fetchShares = async (userId: string) => {
    try {
      const response = await getSocialShares(userId);
      if (response.success && response.data) {
        // Make sure we have the data array before mapping
        const formattedShares: Share[] = response.data.map((share: any) => ({
          id: share.id,
          user_id: share.user_id,
          points: share.points,
          platform: share.platform,
          share_link: share.share_link,
          success: share.success,
          created_at: share.created_at
        }));
        setShares(formattedShares);
      } else {
        console.error('Error in shares data:', response.error);
        setShares([]);
      }
    } catch (error) {
      console.error('Error fetching shares:', error);
      setShares([]);
    }
  };
  
  const fetchEvents = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching events:', error);
        return;
      }

      const formattedEvents: Event[] = data.map((event: any) => ({
        id: event.id,
        name: event.event_name,
        schedule: event.schedule,
        details: event.details,
        location: event.location,
        points: event.points,
        points_per_share: event.points_per_share
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleCreateEvent = () => {
    fetchEvents(userId || "");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-700"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg mb-4">Please sign in to view your profile.</p>
        <Button onClick={() => navigate('/login')}>Sign In</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen overflow-x-hidden bg-black text-white p-0 m-0 pt-28">
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Avatar className="h-16 w-16 mr-4">
              <AvatarImage src={user.user_metadata?.avatar_url || user.user_metadata?.picture} alt={profile?.name || 'User'} />
              <AvatarFallback className="bg-primary/80">
                {(profile?.name?.[0] || user.email?.[0] || 'U').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold">{profile?.name || user.email?.split('@')[0] || 'User'}</h1>
              <p className="text-sm text-gray-400">User ID: {user.id}</p>
              {isMerchant && merchantInfo && (
                <Link 
                  to={`/merchant/${merchantInfo.id}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1 hover:bg-yellow-200"
                >
                  <Store className="w-3 h-3 mr-1" />
                  {merchantInfo.business_name}
                </Link>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={handleSignOut}>Sign Out</Button>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="recordings">Videos</TabsTrigger>
            <TabsTrigger value="points">Points</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="shares">Shares</TabsTrigger>
            {isMerchant && <TabsTrigger value="merchant">Merchant Profile</TabsTrigger>}
          </TabsList>
          
          <TabsContent value="recordings" className="space-y-4">
            {userId && <RecordingsTab userId={userId} />}
          </TabsContent>
          
          <TabsContent value="points" className="space-y-4">
            <PointsTab 
              userId={userId} 
              totalPoints={totalPoints} 
              userName={profile?.name || user.email?.split('@')[0] || 'User'} 
            />
          </TabsContent>
          
          <TabsContent value="events" className="space-y-4">
            <EventsTab 
              userId={userId || ""} 
              events={events}
              onCreateEvent={handleCreateEvent}
              totalPoints={totalPoints}
            />
          </TabsContent>
          
          <TabsContent value="shares" className="space-y-4">
            <SharesTab shares={shares} userId={userId} />
          </TabsContent>
          
          {isMerchant && (
            <TabsContent value="merchant" className="space-y-4">
              <MerchantProfileTab 
                userId={userId || ""} 
                merchantInfo={merchantInfo} 
                onProfileUpdate={() => fetchMerchantInfo(userId || "")}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
